import { Button, Flex, Form } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import { type ValidateErrorEntity } from "rc-field-form/es/interface"
import React, { useRef, useState } from "react"

import { FormChangedWatcher } from "../../../../components/Forms/FormChangedWatcher/FormChangedWatcher.tsx"
import { DocumentPaymentScheduleSection } from "../../../../components/Proposal&Contracts/DocumentPaymentScheduleSection.tsx"
import { DocumentScopeSection } from "../../../../components/Proposal&Contracts/DocumentScopeSection/DocumentScopeSection.tsx"
import { type DocumentScopeSectionListRef } from "../../../../components/Proposal&Contracts/DocumentScopeSection/DocumentScopeSectionList.tsx"
import { type DocumentScopeSectionProjectTypes } from "../../../../components/Proposal&Contracts/document.types.ts"
import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import { hasFieldErrors } from "../../../../utils/form/has-field-errors.ts"
import { type ProposalFormData } from "./proposal-form-data.tsx"
import { ProposalClientInformationSection } from "./sections/ProposalClientInformationSection.tsx"
import { ProposalClientResponsibilitiesSection } from "./sections/ProposalClientResponsibilitiesSection.tsx"
import { ProposalCompanySection } from "./sections/ProposalCompanySection.tsx"
import { ProposalCostSummarySection } from "./sections/ProposalCostSummarySection.tsx"
import { ProposalExclusionsSection } from "./sections/ProposalExclusionsSection.tsx"
import { ProposalInclusionsSection } from "./sections/ProposalInclusionsSection.tsx"
import { ProposalProjectOverviewSection } from "./sections/ProposalProjectOverviewSection.tsx"
import { ProposalTermsSection } from "./sections/ProposalTermsSection.tsx"
import { ProposalWarrantySection } from "./sections/ProposalWarrantySection.tsx"

interface ProposalFormProps {
  innerForm?: FormInstance<ProposalFormData>
  onSubmit: (values: Partial<ProposalFormData>) => void
  projectTypes: DocumentScopeSectionProjectTypes
  initialValues?: Partial<ProposalFormData>
  checkDirty?: boolean
  saving?: boolean
}

const ProposalForm = ({
  innerForm,
  onSubmit,
  saving,
  initialValues,
  projectTypes = [],
  checkDirty = true
}: ProposalFormProps) => {
  const { normalizeDate } = useDateFormat()
  const [form] = Form.useForm<ProposalFormData>(innerForm)
  const detailedScopeListRef = useRef<DocumentScopeSectionListRef>(null)
  const [hasErrors, setHasErrors] = useState(false)

  const onSubmitHandler = ({ project, ...data }: ProposalFormData) => {
    onSubmit({
      ...initialValues,
      ...data,
      project: {
        ...project,
        start_date: normalizeDate(project?.start_date),
        completion_date: normalizeDate(project?.completion_date)
      }
    })
  }

  const onFinishFailed = async (e: ValidateErrorEntity<ProposalFormData>) => {
    const firstError = e?.errorFields?.[0]
    const isPhaseError = firstError?.name?.includes(
      "project_phases" as keyof ProposalFormData
    )

    if (!isPhaseError) {
      return form.scrollToField(firstError?.name, {
        scrollMode: "always",
        behavior: "smooth",
        block: "center"
      })
    }

    const phaseIndex = firstError?.name?.[1] as number
    const phases = e?.values?.project_phases || []
    const projectType = phases?.[phaseIndex]?.project_type

    if (!projectType) return

    detailedScopeListRef?.current?.setCollapsedActiveKey([projectType])
    await new Promise((res) => setTimeout(res, 300))
    form.scrollToField(firstError?.name, {
      scrollMode: "always",
      behavior: "smooth",
      block: "center"
    })
  }

  return (
    <Form<ProposalFormData>
      form={form}
      clearOnDestroy
      onFinish={onSubmitHandler}
      onFinishFailed={onFinishFailed}
      onFieldsChange={(_, allFields) => setHasErrors(hasFieldErrors(allFields))}
      name="dynamic_form_item"
      layout="vertical"
      // scrollToFirstError={{
      //   behavior: "smooth",
      //   block: "center",
      //   scrollMode: "always"
      // }}
      className={"flex flex-col gap-3 pr-2 with-default-error"}
      validateTrigger={["onSubmit", "onChange"]}
      disabled={saving}
      initialValues={{
        ...initialValues,
        cost_summary: {
          ...initialValues?.cost_summary,
          line_items: initialValues?.cost_summary?.line_items || []
        },
        project_phases: initialValues?.project_phases || [],
        inclusions: initialValues?.inclusions || [],
        exclusions: initialValues?.exclusions || [],
        client_responsibilities: initialValues?.client_responsibilities || [],
        terms_and_conditions: initialValues?.terms_and_conditions || [],
        payment_schedule: initialValues?.payment_schedule || [],
        warranty: {
          description: initialValues?.warranty?.description || "",
          items: initialValues?.warranty?.items || []
        }
      }}
    >
      <FormChangedWatcher<ProposalFormData>
        form={form}
        hidden={!checkDirty}
        debug
      />
      <ProposalCompanySection form={form} />
      <ProposalClientInformationSection />
      <ProposalProjectOverviewSection form={form} />
      <DocumentScopeSection
        listRef={detailedScopeListRef}
        form={form}
        projectTypes={projectTypes}
      />

      <ProposalInclusionsSection />
      <ProposalExclusionsSection />
      <ProposalClientResponsibilitiesSection />
      <ProposalCostSummarySection form={form} />
      <DocumentPaymentScheduleSection form={form} />
      <ProposalWarrantySection />
      <ProposalTermsSection />

      {/* register in the form */}
      <Form.Item<ProposalFormData> name="visible_blocks" hidden>
        <input />
      </Form.Item>

      {/* Submit Button */}
      <Flex justify={"end"} className={"sticky z-10 bottom-0 bg-background"}>
        <Button
          loading={saving}
          disabled={hasErrors}
          type="primary"
          htmlType="submit"
        >
          Submit Proposal
        </Button>
      </Flex>
    </Form>
  )
}

export default ProposalForm
